import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { debounce, difference, compact, get, isEmpty, sortBy } from 'lodash';
import OfferCard from './OfferCard';
import OfferPopup from './OfferPopup';
import OfferDeviceCategories from './OfferDeviceCategories';
import OfferDevicePopup from './OfferDevicePopup';
import StickyOffers from './StickyOffers';
import Loading from '../Loading';
import OffersNotAvailable from './OffersNotAvailable';
import {
  checkImpression,
  offersLengthByPlatform,
  areOffersVisible,
} from './utils';
import GDPR from '../../components/gdpr';
import views from '../../components/views';
import { OFFERS_PLATFORMS } from '../../constants';

export default function Offers() {
  const isSDK = Pollfish.helper.isSDK();
  const savedDevices = Pollfish.helper.getOffersSavedDevices();
  const { offers } = Pollfish.offerwallPage;
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showSelectDevicePopup, setShowSelectDevicePopup] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(
    savedDevices.length === 0 ? OFFERS_PLATFORMS.WEB : OFFERS_PLATFORMS.ALL,
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setImpression = () => {
    if (Pollfish.helper.isDebugMode() || !areOffersVisible()) {
      return;
    }
    const offerIds = checkImpression();
    const missingOfferIds = compact(
      difference(offerIds, Pollfish.store.monitoring.offersImpression),
    );
    Pollfish.store.monitoring.offersImpression = [
      ...Pollfish.store.monitoring.offersImpression,
      ...missingOfferIds,
    ];

    if (!isEmpty(missingOfferIds)) {
      Pollfish.mobile.interface.sendToServer(
        '/v2/device/offerwall/set/offers/viewed',
        `{"o_ids": "${missingOfferIds}"}`,
        false,
      );
    }
  };

  useEffect(() => {
    setImpression();
    const debounced = debounce(setImpression, 50);
    const stickyOffersWrapper = document.querySelector(
      '.offerwall-offers-sticky-wrapper',
    );
    window.addEventListener('scroll', debounced);
    if (stickyOffersWrapper) {
      stickyOffersWrapper.addEventListener('scroll', debounced);
    }
    return () => {
      window.removeEventListener('scroll', debounced);
      if (stickyOffersWrapper) {
        stickyOffersWrapper.removeEventListener('scroll', debounced);
      }
    };
  }, [offers, setImpression]);

  const showOfferPopup = useCallback(
    (id) => {
      if (!Pollfish.helper.isDebugMode()) {
        Pollfish.mobile.interface.sendToServer(
          '/v2/device/offerwall/set/offer/accept',
          `{"o_id": "${id}"}`,
          false,
        );
      }

      const offer = offers.find((item) => id === item.id);
      if (!Pollfish.helper.hasAcceptedTerms()) {
        Pollfish.store.setSelectedOffer(offer);
        // eslint-disable-next-line
        new GDPR({
          currentPage: views.common.gdpr,
          goToOfferwall: true,
        }).render();
        return;
      }

      Raven.captureBreadcrumb({
        message: 'openOfferPopup',
        category: 'openOfferPopup',
        data: { offer },
      });
      setSelectedOffer(offer);
      document.body.classList.add('noscroll');
    },
    [offers],
  );

  const hideOfferPopup = useCallback(() => {
    Raven.captureBreadcrumb({
      message: 'hideOfferPopup',
      category: 'hideOfferPopup',
      data: {},
    });
    setSelectedOffer(null);
    document.body.classList.remove('noscroll');
  }, []);

  const showDevicePopup = useCallback(() => {
    Raven.captureBreadcrumb({
      message: 'openChangeDevicePopup',
      category: 'openChangeDevicePopup',
      data: {},
    });
    setShowSelectDevicePopup(true);
    document.body.classList.add('noscroll');
  }, []);

  const hideDevicePopup = useCallback(() => {
    Raven.captureBreadcrumb({
      message: 'hideChangeDevicePopup',
      category: 'hideChangeDevicePopup',
      data: {},
    });
    setShowSelectDevicePopup(false);
    document.body.classList.remove('noscroll');
  }, []);

  const nonStickyFilteredOffers = useMemo(() => {
    const filtered = sortBy(
      offers.filter((offer) => !offer.sticky),
      ['rank'],
    );
    if (selectedDevice === OFFERS_PLATFORMS.ALL) {
      return filtered;
    }
    return offersLengthByPlatform(filtered, selectedDevice);
  }, [offers, selectedDevice]);

  const stickyFilteredOffers = useMemo(() => {
    const filtered = sortBy(
      offers.filter((offer) => offer.sticky),
      ['rank'],
    );
    if (selectedDevice === OFFERS_PLATFORMS.ALL) {
      return filtered;
    }
    return offersLengthByPlatform(filtered, selectedDevice);
  }, [offers, selectedDevice]);

  if (!get(Pollfish, 'offerwallPage.finishedOffersRequest', false)) {
    return <Loading />;
  }

  if (!offers || isEmpty(offers)) {
    return <OffersNotAvailable />;
  }

  return (
    <div>
      {selectedOffer && (
        <OfferPopup onHide={hideOfferPopup} offer={selectedOffer} />
      )}
      {showSelectDevicePopup && (
        <OfferDevicePopup
          onHide={hideDevicePopup}
          savedDevices={savedDevices}
        />
      )}
      {!isSDK && (
        <OfferDeviceCategories
          offers={offers}
          savedDevices={savedDevices}
          selected={selectedDevice}
          onSelect={setSelectedDevice}
          onChange={showDevicePopup}
        />
      )}
      {stickyFilteredOffers.length > 0 && (
        <StickyOffers offers={stickyFilteredOffers} onShow={showOfferPopup} />
      )}
      <section className="container">
        <div className="flex flex-1 flex-wrap margin-sm row">
          {nonStickyFilteredOffers.map((offer) => (
            <OfferCard key={offer.id} {...offer} onShow={showOfferPopup} />
          ))}
        </div>
      </section>
    </div>
  );
}
